import { Search, X } from "lucide-react";
import { useCallback, useState, useEffect } from "react";
import { Box, IconButton, Input, Typography } from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";
import type { GridApi } from "ag-grid-community";

import { Pagination } from "../../shared";

interface PaginationControls {
    currentPage: number;
    totalPage: number;
}

interface GridQuickControlProps {
    gridApi: GridApi | null;
    paginationControls: PaginationControls;
    customToolbarActions?: React.ReactNode;
}

export function GridQuickControl({ gridApi, paginationControls, customToolbarActions }: GridQuickControlProps) {
    const { currentPage, totalPage } = paginationControls;
    const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 600;
            setIsMobile(mobile);
            if (!mobile) setIsSearchExpanded(false);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleSearchChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (gridApi) {
                const searchValue = event.target.value;
                if (typeof (gridApi as any).setGridOption === "function") {
                    (gridApi as any).setGridOption("quickFilterText", searchValue);
                } else {
                    (gridApi as any).setQuickFilter?.(searchValue);
                }
            }
        },
        [gridApi],
    );

    const handlePageChange = useCallback(
        (_event: React.ChangeEvent<unknown>, page: number) => {
            if (gridApi) {
                gridApi.paginationGoToPage(page - 1);
            }
        },
        [gridApi],
    );

    const showPagination = totalPage > 1;

    return (
        <Box
            py={1.5}
            px={2}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"space-between"}
            className="grid-toolbox"
            sx={{ minHeight: "56px" }}
        >
            <AnimatePresence mode="wait">
                {isMobile && isSearchExpanded ? (
                    <motion.div
                        key="search-expanded"
                        initial={{ width: 0, opacity: 0 }}
                        animate={{ width: "100%", opacity: 1 }}
                        exit={{ width: 0, opacity: 0 }}
                        style={{ display: "flex", alignItems: "center", width: "100%" }}
                    >
                        <Input
                            autoFocus
                            fullWidth
                            variant="soft"
                            placeholder="Search projects..."
                            startDecorator={<Search size={18} />}
                            endDecorator={
                                <IconButton size="sm" variant="plain" onClick={() => setIsSearchExpanded(false)}>
                                    <X size={18} />
                                </IconButton>
                            }
                            onChange={handleSearchChange}
                            sx={{
                                "--Input-radius": "12px",
                                bgcolor: "background.surface",
                            }}
                        />
                    </motion.div>
                ) : (
                    <Box
                        key="default-view"
                        component={motion.div}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}
                    >
                        <Box display={"flex"} alignItems={"center"} gap={1} flex={1}>
                            {customToolbarActions && (
                                <Box display={"flex"} alignItems={"center"} gap={1} mr={1}>
                                    {customToolbarActions}
                                    <Box
                                        sx={{ borderLeft: "1px solid", borderColor: "divider", height: 16, mx: 0.5 }}
                                    />
                                </Box>
                            )}

                            {!isMobile ? (
                                <>
                                    <Search size={14} />
                                    <input
                                        className="global-search-input"
                                        placeholder="Search..."
                                        onChange={handleSearchChange}
                                        disabled={!gridApi}
                                    />
                                </>
                            ) : (
                                <IconButton
                                    variant="soft"
                                    color="neutral"
                                    size="sm"
                                    onClick={() => setIsSearchExpanded(true)}
                                    sx={{ borderRadius: "8px" }}
                                >
                                    <Search size={18} />
                                </IconButton>
                            )}
                        </Box>

                        {showPagination && (
                            <Pagination
                                variant="plain"
                                shape="rounded"
                                size="sm"
                                count={totalPage}
                                page={currentPage}
                                onChange={handlePageChange}
                                disabled={!gridApi}
                            />
                        )}
                    </Box>
                )}
            </AnimatePresence>
        </Box>
    );
}
